import { useEffect, useSyncExternalStore } from "react"
import {
  BrowserPrintDevice,
  BrowserPrintUnavailableError,
  getDefaultPrinter,
} from "./browserPrint"

/**
 * LOCKER-522 — one Browser Print health probe shared by every print button on
 * the page, so a locker with 40 doors does not run 40 probes.
 *
 * Probing is opt-in per mounted consumer (`useBrowserPrintHealth(enabled)`):
 * a request to localhost makes Chrome ask the clerk for the "local network /
 * apps on this device" permission, so a tenant that does not print must never
 * trigger one.
 */
export type BrowserPrintHealthStatus =
  | "checking"
  | "unavailable"
  | "noPrinter"
  | "ready"

export type BrowserPrintHealth = {
  status: BrowserPrintHealthStatus
  printer?: BrowserPrintDevice
  error?: string
  checkedAt?: number
}

export const BROWSER_PRINT_POLL_MS = 30_000

type Listener = () => void

let state: BrowserPrintHealth = { status: "checking" }
const listeners = new Set<Listener>()
let activeConsumers = 0
let pollTimer: ReturnType<typeof setInterval> | undefined
let inFlight: Promise<BrowserPrintHealth> | undefined

const setState = (next: BrowserPrintHealth) => {
  state = next
  listeners.forEach((l) => l())
}

export const getBrowserPrintHealth = () => state

export const checkBrowserPrintHealth = (): Promise<BrowserPrintHealth> => {
  if (inFlight) return inFlight
  inFlight = (async () => {
    let next: BrowserPrintHealth
    try {
      const printer = await getDefaultPrinter()
      next = printer
        ? { status: "ready", printer, checkedAt: Date.now() }
        : { status: "noPrinter", checkedAt: Date.now() }
    } catch (err) {
      next = {
        status:
          err instanceof BrowserPrintUnavailableError
            ? "unavailable"
            : "noPrinter",
        error: err instanceof Error ? err.message : String(err),
        checkedAt: Date.now(),
      }
    }
    setState(next)
    return next
  })().finally(() => {
    inFlight = undefined
  })
  return inFlight
}

const onFocus = () => {
  void checkBrowserPrintHealth()
}

const startPolling = () => {
  void checkBrowserPrintHealth()
  pollTimer = setInterval(checkBrowserPrintHealth, BROWSER_PRINT_POLL_MS)
  window.addEventListener("focus", onFocus)
}

const stopPolling = () => {
  clearInterval(pollTimer)
  pollTimer = undefined
  window.removeEventListener("focus", onFocus)
}

/** Registers a consumer; the first one starts polling, the last one stops it. */
export const acquireBrowserPrintHealth = () => {
  activeConsumers += 1
  if (activeConsumers === 1) startPolling()
  return () => {
    activeConsumers -= 1
    if (activeConsumers === 0) stopPolling()
  }
}

const subscribe = (listener: Listener) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Test seam. */
export const resetBrowserPrintHealth = () => {
  stopPolling()
  activeConsumers = 0
  inFlight = undefined
  state = { status: "checking" }
}

export const useBrowserPrintHealth = (enabled: boolean) => {
  useEffect(
    () => (enabled ? acquireBrowserPrintHealth() : undefined),
    [enabled],
  )
  return useSyncExternalStore(subscribe, getBrowserPrintHealth)
}
